/**
 * Sales Controls list + waterfall hide-when-0 (Melissa 2026-08-27).
 *
 * Hide is RENDER only. The store still distinguishes `0` vs unset (`null` /
 * `undefined`). A visible row never shows `AUD 0.00` as a live amount.
 *
 * Units are qualifiers (W7 one 商務單位): hide the shared unit only when no
 * remaining amount uses it. Do not render minQtyUnit / maxQtyUnit as their
 * own rows — they inherit `itemDefinedCostUnit`.
 */
import type { Product } from '@/types/api/product';
import type { CommercialRowKey } from './sections/CommercialSection';

export function isQuietAmount(value: number | null | undefined): boolean {
  return value == null || value === 0;
}

export function visibleSalesRowKeys(
  product: Pick<
    Product,
    | 'basePrice'
    | 'currency'
    | 'itemDefinedCost'
    | 'itemDefinedCostUnit'
    | 'minQty'
    | 'minQtyUnit'
    | 'maxQty'
    | 'maxQtyUnit'
  >
): CommercialRowKey[] {
  const showBase = !isQuietAmount(product.basePrice);
  const showCost = !isQuietAmount(product.itemDefinedCost);
  const showMin = !isQuietAmount(product.minQty);
  const showMax = !isQuietAmount(product.maxQty);
  const showUnit = showCost || showMin || showMax;

  const keys: CommercialRowKey[] = [];
  if (showBase) {
    keys.push('basePrice', 'currency');
  }
  if (showCost) keys.push('itemDefinedCost');
  if (showUnit) keys.push('itemDefinedCostUnit');
  if (showMin) keys.push('minQty');
  if (showMax) keys.push('maxQty');
  return keys;
}

/** UI omit: hide 到岸分攤 when it is 0; keep derived 到岸成本 / 定價基準 / 售價. */
export function isQuietLandedAllocationRow(row: { key: string; amount: number }): boolean {
  return row.key === 'landed_allocation' && row.amount === 0;
}

export function visibleWaterfallRows<T extends { key: string; amount: number }>(rows: T[]): T[] {
  return rows.filter((row) => !isQuietLandedAllocationRow(row));
}
